"use client";

import { ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  number: "01",
  title: "FEATURE FILM",
  description: "Leading and supporting roles in major motion pictures, specializing in psychological drama and noir aesthetics.",
  tier: "SCREEN"
}, {
  number: "02",
  title: "TELEVISION DRAMA",
  description: "Recurring and guest star appearances for network and streaming series, bringing intensity to episodic narratives.",
  tier: "EPISODIC"
}, {
  number: "03",
  title: "CLASSICAL THEATER",
  description: "Stage performances ranging from Shakespearean tragedy to contemporary avant-garde productions in major venues.",
  tier: "STAGE"
}, {
  number: "04",
  title: "VOICE PERFORMANCE",
  description: "Character voice-over for animation, high-end commercial narration, and immersive audio drama projects.",
  tier: "STUDIO"
}, {
  number: "05",
  title: "MOTION CAPTURE",
  description: "Physical performance and facial mapping for cinematic gaming experiences and digital character development.",
  tier: "DIGITAL"
}, {
  number: "06",
  title: "PRODUCTION CONSULTING",
  description: "Advisory on character development, script analysis, and dramatic pacing for independent film projects.",
  tier: "CREATIVE"
}];
export default function FilmTelevision() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const ledger = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section data-section-id="5199" id="film-television" className="bg-background text-foreground pt-40 md:pt-60 pb-24 md:pb-40 px-6 md:px-12 lg:px-16 overflow-hidden">
      <div className="max-w-[1600px] mx-auto">
        
        <div ref={header.ref} className={`mb-12 md:mb-16 transition-all duration-700 ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        </div>

        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-20 md:mb-28">
          <div className="lg:col-span-8">
            <h1 className={`font-default font-light uppercase leading-[1.0] tracking-tighter text-foreground transition-all duration-1000 ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            fontSize: "clamp(3rem, 8vw, 6.5rem)"
          }}>
              <span className="inline-block text-transparent" style={{
              WebkitTextStroke: "1px var(--foreground)"
            }}>
                FILM
              </span>
              <br />
              & TELEVISION
              <br />
              CREDITS.
            </h1>
          </div>

          <div className="lg:col-span-4 flex lg:items-end">
            <p className={`font-sans font-light text-muted-foreground text-lg md:text-xl leading-relaxed max-w-[380px] transition-all duration-1000 delay-200 ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              Performance is an act of precision — every glance, every breath, and every silence captured within the frame to tell a singular, haunting story.
            </p>
          </div>
        </div>

        
        <div ref={ledger.ref} className="border-t border-border">
          {services.map((service, i) => <div data-index={i} key={service.number} className={`group relative grid grid-cols-12 gap-4 md:gap-6 items-start md:items-center py-8 md:py-0 md:h-[120px] border-b border-border px-2 md:px-4 transition-all duration-700 hover:bg-card cursor-pointer ${ledger.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          transitionDelay: ledger.isVisible ? `${i * 80}ms` : "0ms"
        }}>
              
              <div className="col-span-2 md:col-span-1">
                <span className="font-mono text-xs md:text-sm text-muted-foreground tracking-wider">
                  {service.number}
                </span>
              </div>

              
              <div className="col-span-10 md:col-span-5 transition-transform duration-500 group-hover:translate-x-2">
                <h3 className="font-default font-medium uppercase text-foreground text-xl md:text-3xl lg:text-[32px] leading-tight tracking-tight">
                  {service.title}
                </h3>
              </div>

              
              <div className="col-span-12 md:col-span-4 col-start-3 md:col-start-auto">
                <p className="font-sans font-light text-sm md:text-base text-muted-foreground leading-relaxed">
                  {service.description}
                </p>
              </div>

              
              <div className="col-span-12 md:col-span-2 col-start-3 md:col-start-auto flex items-center justify-start md:justify-end gap-3">
                <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  {service.tier}
                </span>
                <ArrowRight className="size-4 text-foreground transition-all duration-500 group-hover:translate-x-2 group-hover:scale-110" aria-hidden="true" />
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}